import React, { useState, useEffect, useCallback } from 'react';

const GA_ID = 'G-MCLMKWRCVY';
const CONSENT_COOKIE = 'civic_cookie_consent';
const CONSENT_VERSION = '1';

function getConsent() {
  try {
    const raw = document.cookie.split('; ').find(c => c.startsWith(CONSENT_COOKIE + '='));
    if (!raw) return null;
    return JSON.parse(decodeURIComponent(raw.split('=')[1]));
  } catch { return null; }
}

function setConsentCookie(prefs) {
  const val = encodeURIComponent(JSON.stringify({ ...prefs, v: CONSENT_VERSION }));
  const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${CONSENT_COOKIE}=${val}; expires=${expires}; path=/; SameSite=Lax`;
}

function loadGA() {
  if (document.querySelector(`script[src*="googletagmanager.com/gtag/js?id=${GA_ID}"]`)) return;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GA_ID);
}

function removeGA() {
  const scripts = document.querySelectorAll(`script[src*="googletagmanager.com/gtag"]`);
  scripts.forEach(s => s.remove());
  ['_ga', '_gid', `_ga_${GA_ID.replace('G-', '')}`].forEach(name => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname};`;
  });
  window.dataLayer = [];
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const applyConsent = useCallback((prefs) => {
    setConsentCookie(prefs);
    if (prefs.analytics) { loadGA(); } else { removeGA(); }
    setVisible(false);
    setShowPrefs(false);
  }, []);

  useEffect(() => {
    const saved = getConsent();
    if (saved && saved.v === CONSENT_VERSION) {
      if (saved.analytics) loadGA();
    } else {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] bg-white border-t border-slate-200 shadow-2xl"
      data-testid="cookie-consent-banner"
    >
      <div className="container mx-auto max-w-4xl px-6 py-5">
        {!showPrefs ? (
          <>
            <p className="text-sm text-slate-700 mb-4 leading-relaxed">
              We use cookies to understand how visitors interact with our site. Strictly necessary cookies are always active. You may accept or reject optional cookies, or manage your preferences.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => applyConsent({ analytics: true, marketing: true })}
                className="flex-1 px-5 py-2.5 text-sm font-semibold rounded-md bg-slate-900 text-white hover:bg-slate-800 transition-colors"
                data-testid="cookie-accept-all"
              >
                Accept All
              </button>
              <button
                onClick={() => applyConsent({ analytics: false, marketing: false })}
                className="flex-1 px-5 py-2.5 text-sm font-semibold rounded-md bg-slate-900 text-white hover:bg-slate-800 transition-colors"
                data-testid="cookie-reject-all"
              >
                Reject All
              </button>
              <button
                onClick={() => setShowPrefs(true)}
                className="flex-1 px-5 py-2.5 text-sm font-semibold rounded-md bg-slate-900 text-white hover:bg-slate-800 transition-colors"
                data-testid="cookie-manage-prefs"
              >
                Manage Preferences
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm font-semibold text-slate-800 mb-4">Cookie Preferences</p>
            <div className="space-y-3 mb-5">
              <label className="flex items-center justify-between">
                <span className="text-sm text-slate-700">Strictly Necessary</span>
                <span className="relative inline-block w-10 h-6 rounded-full bg-blue-600 cursor-not-allowed">
                  <span className="absolute left-[18px] top-[2px] w-5 h-5 bg-white rounded-full shadow" />
                </span>
              </label>
              <label className="flex items-center justify-between cursor-pointer" data-testid="cookie-toggle-analytics">
                <span className="text-sm text-slate-700">Analytics</span>
                <button
                  role="switch"
                  aria-checked={analytics}
                  onClick={() => setAnalytics(!analytics)}
                  className={`relative inline-block w-10 h-6 rounded-full transition-colors ${analytics ? 'bg-blue-600' : 'bg-slate-300'}`}
                >
                  <span className={`absolute top-[2px] w-5 h-5 bg-white rounded-full shadow transition-transform ${analytics ? 'left-[18px]' : 'left-[2px]'}`} />
                </button>
              </label>
              <label className="flex items-center justify-between cursor-pointer" data-testid="cookie-toggle-marketing">
                <span className="text-sm text-slate-700">Marketing</span>
                <button
                  role="switch"
                  aria-checked={marketing}
                  onClick={() => setMarketing(!marketing)}
                  className={`relative inline-block w-10 h-6 rounded-full transition-colors ${marketing ? 'bg-blue-600' : 'bg-slate-300'}`}
                >
                  <span className={`absolute top-[2px] w-5 h-5 bg-white rounded-full shadow transition-transform ${marketing ? 'left-[18px]' : 'left-[2px]'}`} />
                </button>
              </label>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => applyConsent({ analytics, marketing })}
                className="flex-1 px-5 py-2.5 text-sm font-semibold rounded-md bg-slate-900 text-white hover:bg-slate-800 transition-colors"
                data-testid="cookie-save-prefs"
              >
                Save Preferences
              </button>
              <button
                onClick={() => setShowPrefs(false)}
                className="flex-1 px-5 py-2.5 text-sm font-semibold rounded-md bg-slate-900 text-white hover:bg-slate-800 transition-colors"
                data-testid="cookie-back"
              >
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
